/**
 * Centralized query keys for React Query in the admin application
 * This file defines all query keys used in the admin application.
 */

import type { GetVideoParams, GetVideoStatsParams } from 'src/types/videos';
import type { GetOrgAuditLogsInput } from 'admin/queries/organization/types';
import type { ListCollectionItemsInput } from 'types/cms';

type VideoPaginateKeyFilter = {
	term?: string;
	withStats?: boolean;
};
interface VideoPaginateKeyParams extends VideoPaginateKeyFilter {
	organizationId: string;
}

/**
 * Query keys for video-related queries
 */
export const videoKeys = {
	all: ['video'] as const,
	detail: (params: GetVideoParams) => [...videoKeys.all, 'detail', params.organizationId, params.videoId] as const,
	stats: ({
		organizationId,
		videoId,
		...params
	}: Optional<GetVideoStatsParams, 'from' | 'to' | 'rangeStat' | 'groupByDate'>) => [
		...videoKeys.all,
		'stats',
		organizationId,
		videoId,
		{ params },
	],
	originalFileLink: (params: { organizationId: string; videoId: string }) =>
		[...videoKeys.all, 'originalFileLink', params.organizationId, params.videoId] as const,
	list: {
		all: () => [...videoKeys.all, 'list'] as const,
		paginate: ({ organizationId, term, withStats }: VideoPaginateKeyParams) => {
			const key = [...videoKeys.list.all(), 'paginate', organizationId] as const;
			const params: VideoPaginateKeyFilter = {};

			if (term) {
				params.term = term;
			}

			if (withStats) {
				params.withStats = true;
			}

			return Object.keys(params).length > 0 ? ([...key, params] as const) : key;
		},
		usage: (organizationId: string) => [...videoKeys.list.all(), 'usage', organizationId] as const,
	},
};

/**
 * Query keys for story-related queries
 */
export const storyKeys = {
	all: ['story'] as const,
	uploadedFile: {
		all: () => [...storyKeys.all, 'uploadedFile'] as const,
		detail: (fileKey: string) => [...storyKeys.uploadedFile.all(), fileKey] as const,
	},
	getUploadedFilePresignedUrl: {
		all: () => [...storyKeys.all, 'uploadedFilePresignedUrl'] as const,
		detail: (fileKey: string) => [...storyKeys.getUploadedFilePresignedUrl.all(), fileKey] as const,
	},
};

/**
 * Query keys for CMS-related queries
 */
export const cmsKeys = {
	all: ['cms'] as const,
	collections: {
		all: () => [...cmsKeys.all, 'collections'] as const,
		list: (organizationId: string) => [...cmsKeys.collections.all(), 'list', organizationId] as const,
		detail: (collectionId: string) => [...cmsKeys.collections.all(), 'detail', collectionId] as const,
	},
	items: {
		all: () => [...cmsKeys.all, 'items'] as const,
		list: ({ collectionId, ...params }: ListCollectionItemsInput) =>
			[...cmsKeys.items.all(), 'list', collectionId, { params }] as const,
	},
	embeddingConfiguration: (collectionId: string) => [...cmsKeys.all, 'embeddingConfiguration', collectionId] as const,
};

const cmsGoogleIntegrationRoot = ['cmsGoogleIntegration'] as const;

export const cmsGoogleIntegrationKeys = {
	all: cmsGoogleIntegrationRoot,
	byCollection: (collectionId: string) => [...cmsGoogleIntegrationRoot, 'byCollection', collectionId] as const,
	syncRuns: (integrationId: string) => [...cmsGoogleIntegrationRoot, 'syncRuns', integrationId] as const,
	sheets: (integrationId: string, spreadsheetId: string) =>
		[...cmsGoogleIntegrationRoot, 'sheets', integrationId, spreadsheetId] as const,
	headers: (integrationId: string, spreadsheetId: string, sheetId: number) =>
		[...cmsGoogleIntegrationRoot, 'headers', integrationId, spreadsheetId, sheetId] as const,
};

/**
 * Query keys for organization-related queries
 */
export const organizationKeys = {
	all: ['organization'] as const,
	auditLogs: {
		all: () => [...organizationKeys.all, 'auditLogs'] as const,
		detail: ({
			organizationId,
			...params
		}: Optional<GetOrgAuditLogsInput, 'action' | 'page' | 'pageSize' | 'organizationId'>) =>
			[...organizationKeys.auditLogs.all(), organizationId, { params }] as const,
	},
	fileUploadUsage: {
		all: () => [...organizationKeys.all, 'fileUploadUsage'] as const,
		detail: (organizationId: string) => [...organizationKeys.fileUploadUsage.all(), organizationId] as const,
	},
	fileUploadUsageReport: {
		all: () => [...organizationKeys.all, 'fileUploadUsageReport'] as const,
		detail: (organizationId: string) => [...organizationKeys.fileUploadUsageReport.all(), organizationId] as const,
	},
	sso: {
		all: () => [...organizationKeys.all, 'sso'] as const,
		detail: (organizationId: string) => [...organizationKeys.sso.all(), organizationId] as const,
	},
};
